/**
 * GlobalRules — organization-wide attendance rules.
 *
 * Migrated from mock data to live API as part of Wave 3.2. The
 * backend exposes one rule row per organization (department_id=null);
 * the endpoint auto-creates it on first GET so the form always has
 * something to render.
 *
 * The organization default work week (rest_days) is EDITED HERE, as of 2026-08-12.
 * It previously lived only in /attendance/work-calendar, which wrote the global rule while
 * having no notion of department or group rules — so its day dots displayed and edited a
 * work week that could govern nobody, one tap at a time and unconfirmed. That page is now
 * a read model and this page owns the setting; department and group pages keep rest_days as
 * a scoped override, which is a different scope rather than a second editor.
 *
 * Saving asks first (RuleSaveConfirm) and, when a SCORING field changed, leaves a notice
 * with a route to the calculation page: an attendance rule decides how a day is counted, and
 * days already computed keep their old results until someone recalculates.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  MenuItem,
  Select,
  Snackbar,
  Stack,
  Switch,
  Tooltip,
  Typography,
} from '@mui/material';
import { CalendarMonthOutlined, Cancel, Edit, InfoOutlined, PeopleAltOutlined, PinDropOutlined, RefreshOutlined, Save } from '@mui/icons-material';
import { ApiError, attendanceApi } from '../../../api';
import { translateApiError } from '../../../i18n/translateApiError';
import { StickyFormActions } from '../../../components/primitives';
import type {
  AttendanceRule,
  AttendanceRuleUpdate,
} from '../../../api/attendance';
import {
  changedScoringKeys,
  DAY_OPTIONS,
  isEveryDayRest,
  isRestDaySelected,
  FULL_WIDTH_SECTION_KEY,
  isWideRuleSection,
  MINUTE_OPTIONS,
  GLOBAL_RULE_SECTIONS,
  type RuleField,
  ruleToFormValues,
} from './ruleSections';
import { RecalcNotice, RuleSaveConfirm } from './RuleSaveConfirm';

// Toggles whose ON state forces an extra step or blocks a punch — enabling any
// of these shows a confirm dialog (see confirmKey). Backend snake_case keys.
const HIGH_IMPACT_TOGGLES = new Set<string>([
  'require_face',
  'require_liveness',
  'gps_required',
  'enforce_geofence',
]);

const GlobalRules: React.FC = () => {
  const { t } = useTranslation();
  const [rule, setRule] = useState<AttendanceRule | null>(null);
  const navigate = useNavigate();
  const [values, setValues] = useState<Record<string, unknown>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  // Toggles that CHANGE WHAT WORKERS EXPERIENCE (force a step or block a punch).
  // Turning one ON pops a confirm dialog explaining the consequence + the
  // smart-default behavior, so an SMB admin doesn't enable it blindly and then
  // read the result as a bug (owner 2026-06-30). Keyed by backend snake_case.
  const [confirmKey, setConfirmKey] = useState<string | null>(null);
  const [confirmSave, setConfirmSave] = useState(false);
  const [savedScoringKeys, setSavedScoringKeys] = useState<string[]>([]);

  const fetchRule = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await attendanceApi.rulesApi.getGlobal();
      setRule(data);
      setValues(ruleToFormValues(data as Record<string, unknown>, GLOBAL_RULE_SECTIONS));
    } catch (e) {
      const msg =
        e instanceof ApiError ? translateApiError(e, t) : t('attendance:globalRules.loadFailed');
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchRule();
  }, [fetchRule]);

  const handleChange = (key: string, value: unknown) =>
    setValues((prev) => ({ ...prev, [key]: value }));

  const handleCancel = () => {
    // Revert any unsaved edits back to the last fetched snapshot.
    if (rule) setValues(ruleToFormValues(rule as unknown as Record<string, unknown>, GLOBAL_RULE_SECTIONS));
    setIsEditing(false);
  };

  // Which SCORING fields this edit would change. Computed before the write so the confirm
  // can name them, and kept afterwards so the post-save notice knows whether a
  // recalculation is actually needed.
  const pendingScoringChanges = changedScoringKeys(
    rule as unknown as Record<string, unknown> | null,
    values,
  );

  /** Save is a two-step action: name the consequence, then write. */
  const requestSave = () => setConfirmSave(true);

  const performSave = async () => {
    setSaving(true);
    try {
      const changed = pendingScoringChanges;
      const updated = await attendanceApi.rulesApi.updateGlobal(
        values as AttendanceRuleUpdate,
      );
      setRule(updated);
      setValues(ruleToFormValues(updated as unknown as Record<string, unknown>, GLOBAL_RULE_SECTIONS));
      setIsEditing(false);
      setConfirmSave(false);
      // Read from the snapshot taken before the write: `rule` has been replaced, so
      // recomputing the diff here would always be empty and the notice would never show.
      setSavedScoringKeys(changed);
      setToast(t('attendance:globalRules.toastSaved'));
    } catch (e) {
      const msg = e instanceof ApiError ? translateApiError(e, t) : t('common:feedback.saveFailed');
      setToast(msg);
      // Leave the dialog open so the admin can retry or cancel; ConfirmDialog re-enables
      // its buttons when onConfirm rejects.
      throw e;
    } finally {
      setSaving(false);
    }
  };

  const renderField = (field: RuleField) => {
    const v = values[field.key];
    // Read-only mode: show the CONFIGURED VALUE as bold, prominent text (a pill
    // for toggles) instead of a grayed-out disabled input, so admins can scan
    // their settings at a glance (owner 2026-06-30). 'days' keeps its chip row.
    if (!isEditing && field.type !== 'days') {
      if (field.type === 'toggle') {
        const on = !!v;
        return (
          <Chip
            label={on ? t('attendance:ruleSections.valueOn') : t('attendance:ruleSections.valueOff')}
            size="small"
            color={on ? 'primary' : 'default'}
            variant={on ? 'filled' : 'outlined'}
            sx={{
              height: 22,
              fontSize: '0.72rem',
              fontWeight: 700,
              borderRadius: 1,
              ...(on ? {} : { color: 'text.disabled', borderColor: 'divider' }),
            }}
          />
        );
      }
      let label = '—';
      if (field.type === 'minutes') {
        const m = typeof v === 'number' ? v : 0;
        label = m >= 60 ? `${m / 60} hour${m > 60 ? 's' : ''}` : `${m} min`;
      } else if (field.type === 'select') {
        const opt = field.options?.find((o) => String(o.value) === String(v));
        label = opt
          ? t(`attendance:ruleSections.options.${opt.i18nKey}`)
          : v != null && v !== '' ? String(v) : '—';
      }
      return (
        <Typography
          sx={{ fontWeight: 700, fontSize: '0.85rem', color: 'text.primary', whiteSpace: 'nowrap' }}
        >
          {label}
        </Typography>
      );
    }
    switch (field.type) {
      case 'minutes':
        return (
          <FormControl size="small" sx={{ minWidth: 130 }}>
            <Select
              value={typeof v === 'number' ? v : ''}
              onChange={(e) => handleChange(field.key, Number(e.target.value))}
              disabled={!isEditing}
              sx={{
                '& .MuiSelect-select': {
                  fontWeight: !isEditing ? 600 : 400,
                  fontSize: '0.8rem',
                },
              }}
            >
              {MINUTE_OPTIONS.map((m) => (
                <MenuItem key={m} value={m}>
                  {m >= 60 ? `${m / 60} hour${m > 60 ? 's' : ''}` : `${m} min`}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );
      case 'toggle':
        return (
          <Switch
            checked={!!v}
            onChange={(e) => {
              // Enabling a high-impact toggle → confirm first. The dialog spells
              // out the consequence + the always-on smart risk policy, so the
              // admin opts in knowingly (owner 2026-06-30).
              if (e.target.checked && HIGH_IMPACT_TOGGLES.has(field.key)) {
                setConfirmKey(field.key);
                return;
              }
              handleChange(field.key, e.target.checked);
            }}
            disabled={!isEditing}
            size="small"
          />
        );
      case 'select':
        return (
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <Select
              value={v ?? ''}
              onChange={(e) => {
                // Coerce numeric-looking strings back to numbers if the
                // option value is numeric; keep strings for enum fields
                // like night_shift_mode.
                const opt = field.options?.find(
                  (o) => String(o.value) === String(e.target.value),
                );
                handleChange(field.key, opt?.value ?? e.target.value);
              }}
              disabled={!isEditing}
              sx={{
                '& .MuiSelect-select': {
                  fontWeight: !isEditing ? 600 : 400,
                  fontSize: '0.8rem',
                },
              }}
            >
              {field.options?.map((o) => (
                <MenuItem key={String(o.value)} value={o.value as string | number}>
                  {t(`attendance:ruleSections.options.${o.i18nKey}`)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );
      case 'days': {
        const selected = Array.isArray(v) ? (v as string[]) : [];
        return (
          <>
          <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
            {DAY_OPTIONS.map((day) => {
              // Case-insensitive: a rule saved before the casing fix still shows its real state.
              const isOn = isRestDaySelected(selected, day.value);
              return (
                <Chip
                  key={day.value}
                  label={t(`attendance:ruleSections.days.${day.i18nKey}`)}
                  size="small"
                  variant={isOn ? 'filled' : 'outlined'}
                  color={isOn ? 'primary' : 'default'}
                  onClick={
                    isEditing
                      ? () =>
                          handleChange(
                            field.key,
                            isOn
                              ? selected.filter(
                                  (d) => d.trim().toLowerCase() !== day.value.toLowerCase(),
                                )
                              : [...selected, day.value],
                          )
                      : undefined
                  }
                  sx={{
                    cursor: isEditing ? 'pointer' : 'default',
                    borderRadius: 1,
                    fontWeight: 600,
                    fontSize: '0.75rem',
                  }}
                />
              );
            })}
          </Box>
          {isEveryDayRest(selected) && (
            <Alert severity="warning" variant="outlined" icon={false}
              sx={{ mt: 1, fontSize: '0.75rem', py: 0.25 }}>
              {t('attendance:ruleSections.allDaysRestWarning')}
            </Alert>
          )}
          </>
        );
      }
      default:
        return null;
    }
  };

  const globalRestDayValues = rule?.rest_days;
  const globalRestDays = new Set(
    (Array.isArray(globalRestDayValues) ? globalRestDayValues : [])
      .map((day) => String(day).slice(0, 3).toLowerCase()),
  );
  const workingDays = DAY_OPTIONS.filter((day) => !globalRestDays.has(day.i18nKey));
  const restDays = DAY_OPTIONS.filter((day) => globalRestDays.has(day.i18nKey));

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        py: '12px',
        width: '100%',
      }}
    >
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', md: 'center' },
          mb: 1.5,
          gap: 1.5,
        }}
      >
        <Box>
          <Typography
            variant="h6"
            sx={{ fontWeight: 600, fontSize: '1.125rem', color: 'text.primary' }}
          >
            {t('attendance:globalRules.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('attendance:globalRules.subtitleDetail')}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1.5} alignItems="center">
          {!isEditing && (
            <Tooltip title={t('common:actions.refresh')}>
              <span>
                <IconButton
                  size="small"
                  onClick={fetchRule}
                  disabled={loading}
                  sx={{
                    border: '1px solid rgba(0,0,0,0.2)',
                    borderRadius: 1,
                    bgcolor: '#ffffff',
                    '&:hover': { bgcolor: '#f5f5f5' },
                  }}
                >
                  <RefreshOutlined fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          )}
          {!isEditing && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<Edit />}
              onClick={() => setIsEditing(true)}
              disabled={loading || !!error}
              sx={{
                borderRadius: 1,
                textTransform: 'none',
                fontSize: '0.875rem',
                color: 'primary.main',
                borderColor: 'primary.main',
                bgcolor: '#ffffff',
                '&:hover': {
                  bgcolor: 'rgba(25,118,210,0.04)',
                  borderColor: 'primary.dark',
                },
              }}
            >
              {t('attendance:rules.editRules')}
            </Button>
          )}
        </Stack>
      </Box>

      {savedScoringKeys.length > 0 && (
        <RecalcNotice changedScoringKeys={savedScoringKeys} onDismiss={() => setSavedScoringKeys([])} />
      )}
      {error && (
        <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            justifyContent: 'center',
            py: 6,
          }}
        >
          <CircularProgress size={28} />
        </Box>
      ) : (
        <>
          <Card
            variant="outlined"
            sx={{ width: '100%', mb: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}
          >
            <Box sx={{ px: 2.5, py: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, flexDirection: { xs: 'column', sm: 'row' }, gap: 1.5 }}>
              <Box>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.25 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.9rem' }}>
                    {t('attendance:globalRules.workWeekSummary.title')}
                  </Typography>
                  <Chip
                    label={t('attendance:globalRules.workWeekSummary.sourceOwned')}
                    size="small"
                    variant="outlined"
                    color="primary"
                    sx={{ height: 20, fontSize: '0.65rem', borderRadius: 1 }}
                  />
                </Stack>
                <Typography variant="caption" color="text.secondary">
                  {t('attendance:globalRules.workWeekSummary.descriptionOwned')}
                </Typography>
              </Box>
              {/* The work week is edited in the section below, on this page. This used to
                  send the admin to Work Calendar, which was the designated writer; that
                  page is now a read model, so the button would have been a dead end. */}
              <Button
                size="small"
                variant="outlined"
                startIcon={<CalendarMonthOutlined />}
                onClick={() => navigate('/attendance/work-calendar')}
                sx={{ borderRadius: 1, textTransform: 'none', whiteSpace: 'nowrap' }}
              >
                {t('attendance:globalRules.workWeekSummary.ctaView')}
              </Button>
            </Box>
            <CardContent sx={{ px: 2.5, pt: 0.5, pb: 2 }}>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
                    {t('attendance:globalRules.workWeekSummary.workingDays')}
                  </Typography>
                  <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
                    {workingDays.map((day) => (
                      <Chip key={day.value} label={t(`attendance:ruleSections.days.${day.i18nKey}`)} size="small" color="primary" sx={{ height: 22, borderRadius: 1, fontSize: '0.7rem' }} />
                    ))}
                  </Stack>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
                    {t('attendance:globalRules.workWeekSummary.restDays')}
                  </Typography>
                  <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
                    {restDays.length > 0 ? restDays.map((day) => (
                      <Chip key={day.value} label={t(`attendance:ruleSections.days.${day.i18nKey}`)} size="small" variant="outlined" sx={{ height: 22, borderRadius: 1, fontSize: '0.7rem' }} />
                    )) : <Typography variant="caption">—</Typography>}
                  </Stack>
                </Box>
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
                {t('attendance:globalRules.workWeekSummary.overrideNote')}
              </Typography>
            </CardContent>
          </Card>
          <Grid container spacing={2}>
          {GLOBAL_RULE_SECTIONS.map((section) => {
            const isWide = isWideRuleSection(section.i18nKey);
            return (
              <Grid
                item
                xs={12}
                md={isWide ? 12 : 6}
                key={section.i18nKey}
              >
                <Card
                  variant="outlined"
                  sx={{
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  <Box sx={{ px: 2.5, py: 1.25 }}>
                    <Typography
                      variant="subtitle2"
                      sx={{
                        fontWeight: 700,
                        fontSize: '0.9rem',
                        color: 'text.primary',
                      }}
                    >
                      {t(`attendance:ruleSections.${section.i18nKey}.title`)}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontSize: '0.7rem' }}
                    >
                      {t(`attendance:ruleSections.${section.i18nKey}.description`)}
                    </Typography>
                  </Box>
                  <CardContent sx={{ px: 2.5, pt: 0.5, pb: 2, flex: 1 }}>
                    {isWide ? (
                      <Grid container rowSpacing={1.5} columnSpacing={6}>
                        {section.fields.map((field) => (
                          <Grid item xs={12} sm={section.fields.length === 1 ? 12 : 6} key={field.key}>
                            <FieldRow field={field} renderField={renderField} />
                          </Grid>
                        ))}
                      </Grid>
                    ) : (
                      <Stack spacing={1.5}>
                        {section.fields.map((field) => (
                          <FieldRow
                            key={field.key}
                            field={field}
                            renderField={renderField}
                          />
                        ))}
                      </Stack>
                    )}
                    {section.i18nKey === FULL_WIDTH_SECTION_KEY && (
                      <Box sx={{ mt: 2, pt: 1.5, borderTop: '1px dashed', borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5, flexWrap: 'wrap' }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.72rem' }}>
                          {t('attendance:globalRules.geofenceHintText')}
                        </Typography>
                        <Button
                          size="small"
                          startIcon={<PinDropOutlined sx={{ fontSize: 16 }} />}
                          onClick={() => navigate('/attendance/geofences/department')}
                          sx={{ borderRadius: 1, textTransform: 'none', fontSize: '0.78rem', whiteSpace: 'nowrap' }}
                        >
                          {t('attendance:globalRules.geofenceHintCta')}
                        </Button>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
          </Grid>
        </>
      )}

      {isEditing && (
        <StickyFormActions
  primary={(
    <Button
    variant="contained"
    size="small"
    startIcon={saving ? <CircularProgress size={14} /> : <Save />}
    onClick={requestSave}
    disabled={saving}
    sx={{ borderRadius: 1, textTransform: 'none', fontSize: '0.875rem' }}
    >
    {t('attendance:globalRules.saveChanges')}
    </Button>
  )}
  secondary={(
    <Button
    variant="outlined"
    size="small"
    startIcon={<Cancel />}
    onClick={handleCancel}
    disabled={saving}
    sx={{
    borderRadius: 1,
    textTransform: 'none',
    fontSize: '0.875rem',
    color: 'text.secondary',
    borderColor: 'divider',
    }}
    >
    {t('common:actions.cancel')}
    </Button>
  )}
/>
      )}

      <Dialog
        open={!!confirmKey}
        onClose={() => setConfirmKey(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2.5 } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.25, fontWeight: 700, fontSize: '1.02rem', pb: 1, pt: 2.5 }}>
          <Box
            sx={{
              width: 34,
              height: 34,
              borderRadius: '50%',
              bgcolor: 'rgba(237,108,2,0.12)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <InfoOutlined sx={{ fontSize: 19, color: 'warning.main' }} />
          </Box>
          {confirmKey ? t(`attendance:ruleConfirm.${confirmKey}.title`) : ''}
        </DialogTitle>
        <DialogContent sx={{ pb: 1 }}>
          <DialogContentText
            sx={{ fontSize: '0.84rem', color: 'text.secondary', whiteSpace: 'pre-line', lineHeight: 1.55 }}
          >
            {confirmKey ? t(`attendance:ruleConfirm.${confirmKey}.body`) : ''}
          </DialogContentText>
          {confirmKey && (
            <Box
              sx={{
                mt: 1.75,
                p: 1.25,
                borderRadius: 1.5,
                bgcolor: 'rgba(237,108,2,0.07)',
                border: '1px solid',
                borderColor: 'rgba(237,108,2,0.25)',
                display: 'flex',
                gap: 1,
                alignItems: 'flex-start',
              }}
            >
              <PeopleAltOutlined sx={{ fontSize: 18, color: 'warning.main', flexShrink: 0, mt: '1px' }} />
              <Box>
                <Typography sx={{ fontWeight: 700, fontSize: '0.72rem', color: 'warning.dark', letterSpacing: 0.2, mb: 0.25 }}>
                  {t('attendance:ruleConfirm.impactLabel')}
                </Typography>
                <Typography sx={{ fontSize: '0.76rem', color: 'text.primary', lineHeight: 1.45 }}>
                  {t(`attendance:ruleConfirm.${confirmKey}.impact`)}
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ pb: 2.5, pt: 1 }}>
          <Button
            onClick={() => setConfirmKey(null)}
            size="small"
            sx={{ borderRadius: 1, textTransform: 'none', color: 'text.secondary', fontWeight: 600 }}
          >
            {t('attendance:ruleConfirm.keepOff')}
          </Button>
          <Button
            variant="contained"
            size="small"
            disableElevation
            onClick={() => {
              if (confirmKey) handleChange(confirmKey, true);
              setConfirmKey(null);
            }}
            sx={{ borderRadius: 1, textTransform: 'none', fontWeight: 600, px: 2 }}
          >
            {t('attendance:ruleConfirm.enable')}
          </Button>
        </DialogActions>
      </Dialog>

      <RuleSaveConfirm
        open={confirmSave}
        changedScoringKeys={pendingScoringChanges}
        scopeLabel={t('attendance:ruleSave.scopeOrganization')}
        onConfirm={performSave}
        onClose={() => setConfirmSave(false)}
      />

      <Snackbar
        open={!!toast}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          severity="info"
          onClose={() => setToast(null)}
          sx={{ width: '100%' }}
        >
          {toast}
        </Alert>
      </Snackbar>
    </Box>
  );
};

const FieldRow: React.FC<{
  field: RuleField;
  renderField: (f: RuleField) => React.ReactNode;
}> = ({ field, renderField }) => {
  const { t } = useTranslation();
  return (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 2,
    }}
  >
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography
        variant="body2"
        sx={{ fontWeight: 600, fontSize: '0.8rem', color: 'text.primary' }}
      >
        {t(`attendance:ruleSections.fields.${field.i18nKey}.label`)}
      </Typography>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ fontSize: '0.68rem', lineHeight: 1.3 }}
      >
        {t(`attendance:ruleSections.fields.${field.i18nKey}.helper`)}
      </Typography>
    </Box>
    <Box sx={{ flexShrink: 0 }}>{renderField(field)}</Box>
  </Box>
);
};

export default GlobalRules;
